import React, { useCallback } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { addHours } from 'date-fns'
import dayjs from 'dayjs'
import {
  Controller,
  useFieldArray,
  useForm,
  UseFormSetValue,
} from 'react-hook-form'
import { InferType } from 'yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import { societyEventCreateSchema } from '../../../../forms/schemas/society_event_create'
import { nearestHourDate, parsedOrNewDate } from '../../../../helpers/date'
import { NOTIFICATION_SETTINGS_VALUE } from '../../../../helpers/notification_settings_options'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { NEvents } from '../../../../interfaces/services/events.interfaces'
import { Button, ButtonVariant } from '../../../common/Button'
import { DateTimeInput } from '../../../common/DateTimeInput'
import { FormControl } from '../../../common/FormControl'
import { TextArea } from '../../../common/TextArea'
import { TextInput } from '../../../common/TextInput'
import { ToastType } from '../../../common/Toast/toast-type'
import { Toggle } from '../../../common/Toggle'
import { societyCreatePostSchema } from '../../../../forms/schemas/society_create_post'
import { createEvent, eventQueries, updateEvent } from '../../../../api/events'
import { IconChoices } from '../../../common/Icon'
import { createDocument } from '../../../../api/documents'
import { theme } from '../../../../theme/theme'
import { DropZone, DropZoneFile } from '../../../common/DropZone'
import { IDocumentModel } from '../../../../interfaces/models/documents.interfaces'
import { NDocuments } from '../../../../interfaces/services/documents.interfaces'

interface EventPlannerProps {
  eventId?: string
  setPostValue: UseFormSetValue<InferType<typeof societyCreatePostSchema>>
  setDisplayEvent: React.Dispatch<React.SetStateAction<boolean>>
  watchSociety: string
  watchEventsIds: (string | undefined)[] | undefined
}

export const EventPlanner = ({
  eventId,
  setPostValue,
  setDisplayEvent,
  watchSociety,
  watchEventsIds,
}: EventPlannerProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { getErrorMessage } = useFormErrorMessage()
  const userId = useAuthenticatedUserId() as string

  const { data: event } = useQuery(eventQueries.getEvent(eventId))

  const showErrorMessage = (): void => {
    const message = event
      ? 'createUpdateEvent.flashMessage.updateEventFailure'
      : 'createUpdateEvent.flashMessage.createEventFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const getDefaultValues = useCallback((): InferType<
    typeof societyEventCreateSchema
  > => {
    return {
      title: event?.title || '',
      location: event?.location || '',
      description: event?.description || '',
      isAllDay: event?.isAllDay || false,
      startDate: event
        ? parsedOrNewDate(event?.startDate)
        : addHours(nearestHourDate(), 2),
      endDate: event
        ? parsedOrNewDate(event?.endDate)
        : addHours(nearestHourDate(), 3),
      notificationSettings: NOTIFICATION_SETTINGS_VALUE.NONE,
      societyId: event?.societyId || watchSociety,
      userId: event?.userId || userId,
      documentsIds: (event?.documents ?? []).map((document) => {
        return {
          id: document.id,
          name: document.name,
        }
      }),
    }
  }, [event, userId, watchSociety])

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyEventCreateSchema),
    defaultValues: getDefaultValues(),
  })

  const watchIsAllDay = watch('isAllDay')
  const watchStartDate = watch('startDate')
  const watchEndDate = watch('endDate')
  const watchSocietyId = watch('societyId')

  const { mutate: mutateEvent, isPending: creatingOrUpdatingEvent } =
    useMutation({
      mutationFn: async (data: InferType<typeof societyEventCreateSchema>) => {
        const _data = {
          ...data,
          documentsIds: data.documentsIds?.map((document) => document.id),
        }
        const _event = event
          ? await updateEvent({
              id: event._id,
              body: _data as NEvents.NPatch.IRequestBody,
            })
          : await createEvent(_data as NEvents.NCreate.IRequestBody)
        if (!_event.data.data) throw new Error()

        return _event.data.data
      },
      onSuccess: (_event) => {
        if (!watchEventsIds?.includes(_event._id)) {
          setPostValue('eventsIds', [...(watchEventsIds || []), _event._id], {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        setDisplayEvent(false)
      },
      onError: (error) => {
        captureException(error)
        showErrorMessage()
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyEventCreateSchema>
  ): Promise<void> => {
    mutateEvent(data)
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const onClose = (): void => {
    setDisplayEvent(false)
  }

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documentsIds',
  })

  const { mutate: mutateCreateDocument } = useMutation({
    mutationFn: createDocument,
    onSuccess: (data) => {
      const doc = data.data.data
      if (doc?._id) {
        append({ id: doc._id, name: doc.name || doc.filename })
      }
    },
  })

  const handleDocumentUpload = async (file: DropZoneFile): Promise<void> => {
    const data = {
      data: file.base64,
      type: file.name.split('.').pop() as IDocumentModel['type'],
      filename: file.name,
      name: file.name,
      societyId: watchSocietyId,
      section: 'event',
    } satisfies NDocuments.NCreate.IRequestBody

    mutateCreateDocument(data)
  }

  const loading = creatingOrUpdatingEvent
  return (
    <div className="flex flex-col gap-4 rounded border border-neutral-80 p-6">
      <div className="flex gap-8">
        <Controller
          control={control}
          name="title"
          render={({ field: { value, name, onChange, onBlur } }) => (
            <FormControl
              className="flex flex-1"
              label={translate('createUpdateEvent.form.labels.title')}
              error={errors.title && getErrorMessage(errors.title)}
              name={name}
            >
              <TextInput
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                error={!!errors.title}
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="location"
          render={({ field: { value, name, onChange, onBlur } }) => (
            <FormControl
              className="flex flex-1"
              label={translate('createUpdateEvent.form.labels.location')}
              error={errors.location && getErrorMessage(errors.location)}
              name={name}
            >
              <TextInput
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                error={!!errors.location}
              />
            </FormControl>
          )}
        />
      </div>
      <Controller
        control={control}
        name="description"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('createUpdateEvent.form.labels.description')}
            error={errors.description && getErrorMessage(errors.description)}
            name={name}
          >
            <TextArea
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              error={!!errors.description}
            />
          </FormControl>
        )}
      />
      <div className="flex gap-8">
        <Controller
          control={control}
          name="startDate"
          render={({ field: { onChange, value, name } }) => (
            <FormControl
              className="flex flex-1"
              label={
                watchIsAllDay
                  ? translate('createUpdateEvent.form.labels.startDay')
                  : translate('createUpdateEvent.form.labels.startDate')
              }
              error={errors.description && getErrorMessage(errors.description)}
              name={name}
            >
              <DateTimeInput
                onChange={(date) => {
                  onChange(date)
                  // startDate should always be before endDate. If the start
                  // reaches or passes the end, move the end to exactly one hour
                  // after the new start.
                  if (watchEndDate && date >= watchEndDate) {
                    setValue('endDate', dayjs(date).add(1, 'hour').toDate())
                  }
                }}
                minDate={new Date()}
                value={value}
                showTimeSelect={!watchIsAllDay}
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="endDate"
          render={({ field: { onChange, value, name } }) => (
            <FormControl
              className="flex flex-1"
              label={
                watchIsAllDay
                  ? translate('createUpdateEvent.form.labels.endDay')
                  : translate('createUpdateEvent.form.labels.endDate')
              }
              error={errors.description && getErrorMessage(errors.description)}
              name={name}
            >
              <DateTimeInput
                onChange={(date) => {
                  onChange(date)
                  // endDate should always be after startDate. If the end reaches
                  // or passes the start, move the start to exactly one hour
                  // before the new end.
                  if (watchStartDate && date <= watchStartDate) {
                    setValue(
                      'startDate',
                      dayjs(date).subtract(1, 'hour').toDate()
                    )
                  }
                }}
                minDate={new Date()}
                value={value}
                showTimeSelect={!watchIsAllDay}
              />
            </FormControl>
          )}
        />
      </div>
      <div className="flex gap-8">
        <Controller
          control={control}
          name="isAllDay"
          render={({ field: { onChange, value, name } }) => (
            <Toggle
              wrapperClassName="w-fit mt-1"
              label={translate('createUpdateEvent.form.labels.isAllDay')}
              name={name}
              enabled={value || false}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div>
        <p style={theme.textVariants.caption} className="mt-2">
          {translate('createUpdateContractForm.headers.documents')}
        </p>
        <Controller
          control={control}
          render={() => (
            <DropZone
              onUpload={(status, file) => {
                if (status === 'accepted') {
                  handleDocumentUpload(file)
                } else {
                  setToastNotification(
                    ToastType.DANGER,
                    translate('flashMessage.somethingWentWrongError')
                  )
                }
              }}
              multiple
            />
          )}
          name="documentsIds"
        />
        <div className="mt-4">
          {fields.map((field, index) => (
            <div
              key={field.name}
              className="flex w-full flex-row items-center space-x-4"
            >
              <Controller
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <FormControl className="w-full" name={`files.${index}.name`}>
                    <div className="flex w-full flex-row gap-4">
                      <TextInput
                        wrapperClassName="w-full"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        disabled
                        error={!!errors.documentsIds?.[index]?.name}
                      />
                      <Button
                        className="h-12 w-12"
                        icon={IconChoices.DELETE_TRASH}
                        variant={ButtonVariant.DANGER}
                        onClick={() => remove(index)}
                      />
                    </div>
                  </FormControl>
                )}
                name={`documentsIds.${index}.name`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 flex flex-shrink-0 flex-wrap items-center justify-end gap-3">
        <Button
          label={translate('common.actions.cancel')}
          onClick={onClose}
          variant={ButtonVariant.TEXT}
        />
        <Button
          variant={ButtonVariant.PRIMARY}
          disabled={!isValid || loading}
          label={translate('common.actions.save')}
          onClick={handleSubmit(onSubmit, onError)}
        />
      </div>
    </div>
  )
}
